import { Check, Plus, Trash2, X } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { Button } from "../../../../../components/ui/common/button";
import { Form } from "../../../../../components/ui/common/form";
import { FormInput } from "../../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../../../components/ui/Input/FormUnitInput/FormUnitInput";
import { PER_WEIGHT_UNITS } from "../../../../../config/optionsConstants";
import { WASTE_MANAGEMENT_TYPE_IDS } from "../../../../../components/wasteManagment/wasteManagement.constants";
import { MgmtOption, WasteFormValues } from "../types/wasteRecords.types";

interface WasteRecordFormProps {
  defaultValues: WasteFormValues;
  onSave: (values: WasteFormValues) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  ingredientOptions: string[];
  mgmtOptions: MgmtOption[];
  isEdit?: boolean;
  title?: string;
}

/** Add/edit form for one waste record — reused for both the inline row editor and the draft panel. */
const WasteRecordForm: React.FC<WasteRecordFormProps> = ({
  defaultValues,
  onSave,
  onCancel,
  onDelete,
  ingredientOptions,
  mgmtOptions,
  isEdit = false,
  title
}) => {
  const methods = useForm<WasteFormValues>({ defaultValues });
  const { control, handleSubmit, watch } = methods;
  const managementType = watch("managementType");
  const ingredientSelectOptions = ingredientOptions.map(name => ({ value: name, label: name }));

  return (
    <Form {...methods}>
      <form onSubmit={handleSubmit(onSave)} className="tw-space-y-2">
        {(title || onDelete) && (
          <div className="tw-flex tw-items-center tw-justify-between">
            {title && <span className="tw-text-xs tw-font-semibold tw-text-foreground">{title}</span>}
            {onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="tw-h-7 tw-w-7 tw-text-destructive"
                onClick={onDelete}
              >
                <Trash2 className="tw-h-3 tw-w-3" />
              </Button>
            )}
          </div>
        )}

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-2">
          <FormInput
            control={control}
            name="streamName"
            label="Management Name"
            required
            errorMessage="Management name is required"
            placeholder="e.g., Whey stream"
          />
          <FormSelect
            control={control}
            name="managementType"
            label="Management Type"
            required
            errorMessage="Management type is required"
            placeholder="Select"
            options={mgmtOptions}
          />
          <FormUnitInput
            control={control}
            name="distribution"
            unitName="distributionUnit"
            label="Distribution"
            placeholder="0"
            unitOptions={PER_WEIGHT_UNITS}
          />
        </div>

        {managementType === WASTE_MANAGEMENT_TYPE_IDS.RECOVERED_FOR_USE_OR_SALE && (
          <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-pt-1 tw-border-t tw-border-border/50">
            {ingredientOptions.length > 0 ? (
              <FormSelect
                control={control}
                name="ingredientName"
                label="Ingredient Name"
                required
                errorMessage="Ingredient is required"
                placeholder="Select ingredient"
                options={ingredientSelectOptions}
              />
            ) : (
              <FormInput
                control={control}
                name="ingredientName"
                label="Ingredient Name"
                placeholder="No ingredients defined yet"
              />
            )}
            <FormInputNumber control={control} name="moisture" label="Moisture %" placeholder="0" />
          </div>
        )}

        <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-1">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tw-h-7 tw-text-xs tw-gap-1"
              onClick={onCancel}
            >
              <X className="tw-h-3 tw-w-3" /> Cancel
            </Button>
          )}
          <Button type="submit" size="sm" className="tw-h-7 tw-text-xs tw-gap-1">
            {isEdit ? (
              <>
                <Check className="tw-h-3 tw-w-3" /> Save
              </>
            ) : (
              <>
                <Plus className="tw-h-3 tw-w-3" /> Save Record
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default WasteRecordForm;
